import { type JSX } from "react";
import type { components } from "../../../api/schema";

type Scene = components["schemas"]["Scene"];

export type SceneNodeProps = {
  scene: Scene;
  /**
   * 骨格 beat に対応するシーンかどうか。true のとき強調表示する。
   * PalimpsestGrid 側で PlotDraft.beats と scene index を照合して判定する。
   */
  isBeat?: boolean;
  /** クリック / タップ時に呼ばれる。 */
  onTap: (scene: Scene) => void;
  /** aria-label に使う補足文字列 (scene index 等)。未指定時は id の先頭 8 文字を使う。 */
  label?: string;
};

/**
 * worldline-palimpsest (Phase 3-1, Task 5.1) palimpsest grid の 1 scene ノード。
 *
 * <button> 要素で描画し、キーボード操作を既定でサポートする (Enter / Space)。
 * beat フラグ on のとき indigo で強調、ending (isEnding) は金色で強調。
 */
export function SceneNode({
  scene,
  isBeat = false,
  onTap,
  label,
}: SceneNodeProps): JSX.Element {
  const shortId = label ?? scene.id.slice(0, 8);
  const isEnding = scene.isEnding;

  const baseClass =
    "inline-flex items-center justify-center w-6 h-6 rounded-full border text-[10px] font-mono transition-colors focus:outline-none focus-visible:ring-2";

  const stateClass = isEnding
    ? "border-amber-400 bg-amber-900/40 text-amber-200 focus-visible:ring-amber-300"
    : isBeat
      ? "border-indigo-400 bg-indigo-900/60 text-indigo-100 focus-visible:ring-indigo-300"
      : "border-slate-600 bg-slate-800/60 text-slate-200 hover:bg-slate-700 focus-visible:ring-slate-300";

  return (
    <button
      type="button"
      data-testid={`palimpsest-scene-node-${scene.id}`}
      data-beat={isBeat ? "true" : undefined}
      data-ending={isEnding ? "true" : undefined}
      className={`${baseClass} ${stateClass}`}
      aria-label={`シーン ${shortId}${isEnding ? " (エンディング)" : ""}${isBeat ? " (骨格ビート)" : ""}`}
      onClick={() => onTap(scene)}
    >
      {isEnding ? "★" : isBeat ? "◆" : "·"}
    </button>
  );
}
